import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { checkJwtExistsAndExpired, getUserInfo, logoutSession } from "../../services/AuthService";
import { localStorageRemoveItem } from "../../common/localStorage";
import { toast } from "react-toastify";
import logo from "../common/images/pi.png";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const Header = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isAuthenticated = checkJwtExistsAndExpired();
    const user = getUserInfo();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const isActive = (path) => location.pathname === path;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownOpen]);

    const handleLogout = async () => {
        try {
            await logoutSession();
            localStorageRemoveItem("jwt");
            localStorageRemoveItem("user");
            toast.success("Logged out successfully");
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
            localStorageRemoveItem("jwt");
            localStorageRemoveItem("user");
            navigate("/");
        }
    };

    return (
        <header className="bg-blue-700 text-white shadow-sm">
            <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                <Link to="/">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center text-lg font-bold">
                            <img src={logo} alt="PiMail" className="w-full h-full object-cover rounded-full" />
                        </div>
                        <span className="text-xl font-semibold">PiMail</span>
                    </div>
                </Link>

                <nav className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm flex-wrap justify-end">
                    <Link
                        to="/"
                        className={`px-2 py-1 rounded ${isActive("/") ? "bg-white text-blue-700" : "hover:bg-white/10"
                            }`}
                    >
                        Mailbox
                    </Link>
                    <Link
                        to="/admin/domains"
                        className={`px-2 py-1 rounded ${isActive("/admin/domains")
                            ? "bg-white text-blue-700"
                            : "hover:bg-white/10"
                            }`}
                    >
                        Domains
                    </Link>
                    {isAuthenticated && user ? (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="max-w-[12rem] sm:max-w-none flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded bg-white/20 hover:bg-white/30 transition-colors"
                            >
                                <span className="text-sm truncate">{user.email}</span>
                                <ChevronDownIcon
                                    className={`w-4 h-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {dropdownOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                                    <div className="px-4 py-2 border-b border-gray-200">
                                        <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                        {user.premium && (
                                            <p className="text-xs text-blue-600 mt-1">Premium User</p>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            className={`px-3 py-1 rounded ${isActive("/login")
                                ? "bg-white text-blue-700"
                                : "bg-white/20 hover:bg-white/30"
                                }`}
                        >
                            Login
                        </Link>
                    )}
                </nav>
            </div>
        </header>
    );
};

export default Header;

